import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Zap, LogOut, Menu, Loader2, LayoutDashboard, BarChart3,
  Workflow, Activity, Store, Shield, Users, Search, Settings,
  TrendingUp, DollarSign, ArrowUpRight, ChevronRight
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Analytics = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [overview, setOverview] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [listings, setListings] = useState([]);
  const [retention, setRetention] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [oRes, fRes, lRes, rRes] = await Promise.all([
          axios.get(`${API}/analytics/overview`),
          axios.get(`${API}/analytics/funnel`),
          axios.get(`${API}/analytics/listings`),
          axios.get(`${API}/analytics/retention`),
        ]);
        setOverview(oRes.data);
        setFunnel(fRes.data);
        setListings(lRes.data);
        setRetention(rRes.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, []);

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Workflow, label: 'Workflow Builder', path: '/workflow' },
    { icon: BarChart3, label: 'Analytics', path: '/analytics' },
    { icon: Shield, label: 'Operations', path: '/operations' },
    { icon: Activity, label: 'Agent Monitor', path: '/agents' },
    { icon: Store, label: 'Marketplace', path: '/marketplace' },
    { icon: Settings, label: 'CRM Settings', path: '/crm-settings' },
  ];

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="noise-overlay" />
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0a0a0a] border-r border-white/5 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5"><Link to="/" className="flex items-center gap-2"><HSLogo size={32} /><span className="font-black text-lg tracking-tight">HumanScaled</span></Link></div>
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map(item => { const Icon = item.icon; const isActive = location.pathname === item.path; return (<Link key={item.path} to={item.path} className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${isActive ? 'bg-emerald-500/10 text-emerald-400 border-l-2 border-emerald-500' : 'text-muted-foreground hover:text-white hover:bg-white/5'}`}><Icon className="w-5 h-5" />{item.label}</Link>); })}
          </nav>
          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-sm mb-3"><div className="w-8 h-8 bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm">{user?.name?.charAt(0) || 'U'}</div><div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{user?.name}</p></div></div>
            <Button variant="ghost" onClick={() => { logout(); navigate('/'); }} className="w-full justify-start text-muted-foreground hover:text-white"><LogOut className="w-4 h-4 mr-2" /> Sign Out</Button>
          </div>
        </div>
      </aside>
      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
          <div className="flex items-center justify-between h-16 px-6">
            <div className="flex items-center gap-4">
              <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-muted-foreground hover:text-white"><Menu className="w-6 h-6" /></button>
              <h1 className="text-lg font-bold" data-testid="analytics-title">Analytics</h1>
            </div>
            <span className="text-xs font-mono text-muted-foreground">{overview?.period_days || 30} day view</span>
          </div>
        </header>

        <div className="p-6 lg:p-8 space-y-6">
          {loading ? <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div> : (
            <>
              {/* Top Stats */}
              {overview && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="overview-stats">
                  {[
                    { label: 'Monthly Revenue', value: `$${overview.revenue?.monthly_contract?.toLocaleString() || 0}`, sub: `$${overview.revenue?.annual_projected?.toLocaleString() || 0}/yr projected`, cls: 'text-emerald-400' },
                    { label: 'Live Agents', value: `${overview.agents?.live || 0}/${overview.agents?.total || 0}`, sub: `${overview.runs?.total || 0} runs (${overview.runs?.success_rate || 0}% success)`, cls: 'text-violet-400' },
                    { label: 'Active Clients', value: overview.clients?.total || 0, sub: `${overview.marketplace?.inquiries || 0} marketplace inquiries`, cls: 'text-blue-400' },
                    { label: 'Conversion Rate', value: `${overview.marketplace?.conversion_rate || 0}%`, sub: `${overview.marketplace?.converted || 0} converted of ${overview.marketplace?.inquiries || 0}`, cls: 'text-amber-400' },
                  ].map((stat, i) => (
                    <div key={i} className="bg-[#0a0a0a] border border-white/5 p-5">
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">{stat.label}</p>
                      <p className={`font-mono text-2xl font-bold ${stat.cls}`}>{stat.value}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">{stat.sub}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="grid lg:grid-cols-2 gap-6">
                {/* Conversion Funnel */}
                {funnel && (
                  <div className="bg-[#0a0a0a] border border-white/5 p-5" data-testid="conversion-funnel">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4" /> Inquiry Funnel</h3>
                    <div className="space-y-3">
                      {funnel.funnel.map((stage, i) => {
                        const maxCount = Math.max(...funnel.funnel.map(s => s.count), 1);
                        const width = Math.max((stage.count / maxCount) * 100, 8);
                        const colors = ['bg-blue-500', 'bg-violet-500', 'bg-amber-500', 'bg-emerald-500'];
                        return (
                          <div key={i}>
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="capitalize text-muted-foreground">{stage.stage}</span>
                              <span className="font-mono">{stage.count} <span className="text-muted-foreground">({stage.percentage}%)</span></span>
                            </div>
                            <div className="w-full h-3 bg-white/5">
                              <div className={`h-full ${colors[i % colors.length]} transition-all`} style={{ width: `${width}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {funnel.declined > 0 && <p className="text-xs text-red-400 mt-3">Declined: {funnel.declined}</p>}
                  </div>
                )}

                {/* Retention */}
                {retention && (
                  <div className="bg-[#0a0a0a] border border-white/5 p-5" data-testid="retention-metrics">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2"><Users className="w-4 h-4" /> Client Retention</h3>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase">Retention Rate</p>
                        <p className={`font-mono text-2xl font-bold ${retention.retention_rate >= 80 ? 'text-emerald-400' : 'text-amber-400'}`}>{retention.retention_rate}%</p>
                        <p className="text-[10px] text-muted-foreground">{retention.active_clients}/{retention.total_clients} active</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-muted-foreground uppercase">CR Completion</p>
                        <p className="font-mono text-2xl font-bold text-violet-400">{retention.change_requests?.completion_rate || 0}%</p>
                        <p className="text-[10px] text-muted-foreground">{retention.change_requests?.completed}/{retention.change_requests?.total} completed</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Listing Performance */}
              {listings.length > 0 && (
                <div className="bg-[#0a0a0a] border border-white/5" data-testid="listing-performance">
                  <div className="p-5 border-b border-white/5">
                    <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2"><Store className="w-4 h-4" /> Listing Performance</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-white/5">
                          <th className="text-left p-4 text-xs text-muted-foreground uppercase font-medium">Service</th>
                          <th className="text-left p-4 text-xs text-muted-foreground uppercase font-medium">Price</th>
                          <th className="text-left p-4 text-xs text-muted-foreground uppercase font-medium">Quality</th>
                          <th className="text-left p-4 text-xs text-muted-foreground uppercase font-medium">Inquiries</th>
                          <th className="text-left p-4 text-xs text-muted-foreground uppercase font-medium">Converted</th>
                          <th className="text-left p-4 text-xs text-muted-foreground uppercase font-medium">Rate</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-white/5">
                        {listings.map(l => (
                          <tr key={l.id} className="hover:bg-white/5">
                            <td className="p-4 text-sm font-medium">{l.name}</td>
                            <td className="p-4 font-mono text-sm text-emerald-400">${l.price}/mo</td>
                            <td className="p-4 font-mono text-sm">{l.quality_score}%</td>
                            <td className="p-4 font-mono text-sm">{l.inquiries}</td>
                            <td className="p-4 font-mono text-sm text-emerald-400">{l.converted}</td>
                            <td className="p-4 font-mono text-sm">{l.conversion_rate}%</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Revenue Summary */}
              {overview && (
                <div className="bg-[#0a0a0a] border border-emerald-500/10 p-5" data-testid="revenue-summary">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4 flex items-center gap-2"><DollarSign className="w-4 h-4" /> Revenue Summary</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Monthly Revenue</p>
                      <p className="font-mono text-xl font-bold text-emerald-400">${overview.revenue?.monthly_contract?.toLocaleString() || 0}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Period Costs</p>
                      <p className="font-mono text-xl font-bold text-red-400">${overview.costs?.total_period?.toFixed(2) || 0}</p>
                    </div>
                    <div>
                      <p className="text-[10px] text-muted-foreground uppercase">Net Margin</p>
                      <p className="font-mono text-xl font-bold text-emerald-400">${overview.margin?.monthly?.toLocaleString() || 0}<span className="text-xs text-muted-foreground">/mo</span></p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default Analytics;
